import { fetchWithToken, mutateGraphQL } from '@/services'
import { TIemUpdate } from '../types'

const GET_CART_INFO = `
query getCart($cart_id: String!) {
  cart(cart_id: $cart_id) {
    id
    shipping_information {
      shipping_fee
      order_amount
    }
    affiliate_information {
      cashback
    }
    email
    items {
      id
      uid
      quantity
      prices {
        discounts {
          amount {
            ...MoneyFields
          }
        }
        price {
          ...MoneyFields
        }
        row_total {
          ...MoneyFields
        }
      }
      product {
        __typename

        ... on ConfigurableProduct {
          ...ConfigurableProductField
        }
        name
        sku
        url_key
        image {
          label
          url
        }
        thumbnail {
          url
        }
      }
      quantity
      ... on ConfigurableCartItem {
        configurable_options {
          option_label
          value_label
          configurable_product_option_value_uid
        }
      }
      ... on SimpleCartItem {
        product {
          name
        }
      }
      errors {
        code
        message
      }
    }
    available_payment_methods {
      code
      title
    }
    applied_coupons {
      code
    }
    prices {
      discounts {
        amount {
          ...MoneyFields
        }
        type
        discount_amount
        label
      }
      subtotal_excluding_tax {
        ...MoneyFields
      }
      subtotal_including_tax {
        ...MoneyFields
      }
      subtotal_with_discount_excluding_tax {
        ...MoneyFields
      }
      grand_total {
        ...MoneyFields
      }
    }
    shipping_addresses {
      selected_shipping_method {
        amount {
          currency
          value
        }
        carrier_code
        method_code
        method_title
      }
      ward_code
      district_code
      address_type
      customer_notes
      address_note
      available_shipping_methods {
        amount {
          ...MoneyFields
        }
        method_title
        carrier_code
        carrier_title
        available
        method_code
      }
      city
      firstname
      lastname
      street
      ward
      district
      telephone
      selected_shipping_method {
        amount {
          currency
          value
        }
        carrier_code
        method_title
        method_code
        method_title
      }
    }
  }
}
fragment MoneyFields on Money {
  value
  currency
}
fragment ConfigurableProductField on ConfigurableProduct {
  configurable_options {
    attribute_code
    label
    uid
    attribute_uid
    values {
      label
      uid
      default_label
      swatch_data {
        value
      }
    }
  }
  variants {
    attributes {
      code
      label
      uid
    }
    product {
      __typename
      image {
        label
        url
      }
      thumbnail {
        url
      }
      price_range {
        ...PriceRangeField
      }
      url_key
      sku
      name
    }
  }
}
fragment PriceRangeField on PriceRange {
  __typename
  maximum_price {
    ...ProductPriceField
  }
  minimum_price {
    ...ProductPriceField
  }
}
fragment ProductPriceField on ProductPrice {
  discount {
    ...ProductDiscountFields
  }
  final_price {
    ...MoneyFields
  }
  regular_price {
    ...MoneyFields
  }
}
fragment ProductDiscountFields on ProductDiscount {
  amount_off
  percent_off
}
`

const CREATE_EMPTY_CART = `
mutation createEmptyCart {
    createEmptyCart
  }
`
const MERGE_CART = `
mutation mergeCartGuestToCartCustomer($source_cart_id: String!, $destination_cart_id: String) {
  mergeCarts(
    source_cart_id: $source_cart_id
    destination_cart_id: $destination_cart_id
  ) {
    email
    id
    total_quantity
  }
}`

const ADD_TO_CART = `
mutation addProductToCart($cartId: String!, $cartItems: [CartItemInput!]!) {
  addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
    cart {
      email
      id
      is_virtual
      total_quantity
    }
    user_errors {
      code
      message
    }
  }
} `

const REMOVE_CART_ITEM = `
mutation RemoveItemForMiniCart(
  $removeItemFromCartInput: RemoveItemFromCartInput
) {
  removeItemFromCart(input: $removeItemFromCartInput) {
    cart {
      id
      items {
        uid
        errors {
          code
          message
        }
      }
    }
  }
}
`

const UPDATE_CART = `
mutation updateCartItem($input: UpdateCartItemsInput) {
  updateCartItems(input: $input) {
    cart {
      email
      id
      is_virtual
    }
  }
}
`

const APPLY_COUPON = `
mutation applyCouponToCart($cart_id: String!, $coupon_code: String!) {
  applyCouponToCart(input: { cart_id: $cart_id, coupon_code: $coupon_code }) {
    cart {
      items {
        product {
          sku
          name
        }
        quantity
      }
      applied_coupons {
        code
      }
      prices {
        grand_total {
          value
          currency
        }
      }
    }
  }
}`

const REMOVE_COUPON = `
mutation removeCouponFromCart($input: RemoveCouponFromCartInput) {
   removeCouponFromCart(input: $input) {
  cart {
     id
   is_virtual
   total_quantity
 }
 }
}`

const APPLY_MEMBERSHIP_POINT = `
mutation applyMembershipPointToCart($point: Int!, $cart_id: String!) {
  applyMembershipPointToCart(point: $point, cart_id: $cart_id)
}
`

const SET_GUEST_EMAIL = `
mutation SetGuestEmail($input: SetGuestEmailOnCartInput) {
    setGuestEmailOnCart(input: $input) {
        cart {
            id
            email
        }
    }
}
`

const setEmailToCartGuest = async (email: string, cartId: string) => {
  try {
    const response = await mutateGraphQL({
      query: SET_GUEST_EMAIL,
      variables: {
        input: {
          email: email,
          cart_id: cartId,
        },
      },
    })
    const errorMessage = response?.payload?.errors?.[0]?.message
    if (errorMessage) {
      throw new Error(errorMessage)
    }
    return response?.payload?.data?.setGuestEmailOnCart?.cart?.email
  } catch (error) {
    console.error('Error setting guest email:', error)
    return { error }
  }
}

const createEmptyCart = async () => {
  try {
    const res = await fetchWithToken(
      {
        query: CREATE_EMPTY_CART,
      },
      {
        next: { tags: ['cart-guest'] },
      }
    )
    const id = res?.payload?.data?.createEmptyCart
    return id
  } catch (err) {
    console.log('Error when creating empty cart: ', err)
    return null
  }
}

const mergeCartGuestToUser = async (source_cart_id: string) => {
  try {
    let idCartCustomer = await fetchWithToken(
      {
        query: CREATE_EMPTY_CART,
      },
      {
        next: { tags: ['cart-customer'] },
      }
    ).then((res) => res?.payload?.data?.createEmptyCart)
    const res = await fetchWithToken({
      query: MERGE_CART,
      variables: {
        source_cart_id: source_cart_id, // id cart guest
        destination_cart_id: idCartCustomer, //id cart customer
      },
    })
    const id = res?.payload?.data?.mergeCarts?.id
    if (!id) return { data: null, status: 'error', message: res?.payload?.errors?.[0]?.message }
    return id
  } catch (err) {
    console.log("Error when merging guest's cart to user's cart: ", err)
    return null
  }
}

const addToCartAction = async (items: ICartItem[], cartId: string) => {
  try {
    const res = await fetchWithToken({
      query: ADD_TO_CART,
      variables: {
        cartId: cartId,
        cartItems: items,
      },
    })
    if (res?.payload?.errors?.length) {
      throw new Error(
        res?.payload?.errors?.[0]?.debugMessage ||
          res?.payload?.errors?.[0]?.message ||
          'Xảy ra lỗi khi thêm sản phẩm vào giỏ hàng'
      )
    }
    const id = res?.payload?.data?.addProductsToCart
    return id
  } catch (err) {
    console.log('Xảy ra lỗi khi thêm sản phẩm vào giỏ hàng: ', (err as Error)?.message)
    return {
      user_errors: [{ message: (err as Error)?.message || 'Xảy ra lỗi khi thêm sản phẩm vào giỏ hàng' }],
    }
  }
}

const removeCartItem = async (cartId: string, cartItemUID: string) => {
  try {
    const res = await fetchWithToken({
      query: REMOVE_CART_ITEM,
      variables: {
        removeItemFromCartInput: {
          cart_id: cartId,
          cart_item_uid: cartItemUID,
        },
      },
    }).then((res) => res?.payload)
    const data = res?.data?.removeItemFromCart
    if (!data) return { data: null, message: res?.errors?.[0]?.message, status: 'error' }
    return data
  } catch (err) {
    console.log('Error when adding items to cart: ', err)
    return null
  }
}

const updateCart = async (cartId: string, dataItem: Record<string, string | boolean>) => {
  try {
    const res = await fetchWithToken({
      query: UPDATE_CART,
      variables: {
        input: {
          cart_id: cartId,
          cart_items: [
            {
              cart_item_uid: dataItem?.cart_item_uid,
              quantity: dataItem?.quantity,
              available_to_checkout: dataItem?.available_to_checkout,
            },
          ],
        },
      },
    }).then((res) => res?.payload)
    const data = res?.data?.updateCartItems
    if (!data) return { data: null, message: res?.errors?.[0]?.message, status: 'error' }
    return data
  } catch (err) {
    console.log('Error when adding items to cart: ', err)
    return null
  }
}

const updateCartAll = async (cartId: string, dataItem: TIemUpdate[]) => {
  try {
    const res = await fetchWithToken({
      query: UPDATE_CART,
      variables: {
        input: {
          cart_id: cartId,
          cart_items: dataItem,
        },
      },
    }).then((res) => res?.payload)
    const data = res?.data?.updateCartItems
    if (!data) return { data: null, message: res?.errors?.[0]?.message, status: 'error' }
    return data
  } catch (err) {
    console.log('Error when adding items to cart: ', err)
    return null
  }
}

const getCart = async (cartId: string) => {
  try {
    const res = await fetchWithToken(
      {
        query: GET_CART_INFO,
        variables: {
          cart_id: cartId,
        },
      },
      {
        next: { tags: ['cart-info'] },
      }
    )
    const data: ICart = res?.payload?.data?.cart
    return data
  } catch (err) {
    console.log('Error when adding items to cart: ', err)
    return null
  }
}

const applyMembershipPoint = async (point: number, cartId: string) => {
  try {
    const res = await fetchWithToken({
      query: APPLY_MEMBERSHIP_POINT,
      variables: {
        point: point,
        cart_id: cartId,
      },
    })
    
    const errorMessage = res?.payload?.errors?.[0]?.message
    if (errorMessage) {
      return { status: 'error', message: errorMessage }
    }
    
    const success = res?.payload?.data?.applyMembershipPointToCart
    if (success) {
      return { status: 'success', message: 'Áp dụng điểm thành công' }
    }
    
    return { status: 'error', message: 'Có lỗi xảy ra khi áp dụng điểm' }
  } catch (error) {
    console.error('Error applying membership point:', error)
    return { status: 'error', message: 'Có lỗi xảy ra khi áp dụng điểm' }
  }
}

export {
  addToCartAction,
  CREATE_EMPTY_CART,
  createEmptyCart,
  mergeCartGuestToUser,
  removeCartItem,
  updateCart,
  getCart,
  updateCartAll,
  setEmailToCartGuest,
  applyMembershipPoint,
}
